import { computed } from 'vue';
import { defineStore } from 'pinia';
import { usePlannerStore } from '@/views/3d-planner/store/plannerStore';
import { usePlannerSceneViewModel } from '@/views/3d-planner/store/plannerSceneViewModel';
import { useActivePresetTracker } from '@/views/3d-planner/composables/useActivePresetTracker';
import { catstackPlaneIds, type CatstackPlaneId } from '@/domain/anatomy/catstackPlane';
import { nextSliceIndex } from '@/util/ctSliderWheel';
import type {
    VisibilityToggleId,
    VisibilityToggles,
} from '@/views/3d-planner/measurements/visibilityToggles';

const PLANE_LABELS: Record<CatstackPlaneId, string> = {
    axial: 'CT Axial',
    coronal: 'CT Coronal',
    sagittal: 'CT Sagittal',
};

/** Per-plane slider state the View binds to via `vm.planeStates[planeId]`. */
type PlaneState = {
    label: string;
    sliderValue: number;
    displayIndex: number;
    max: number;
    visible: boolean;
};

/** Structural, not `CatstacksData` — a ViewModel must not import `@/api/*` (R-C). */
interface CatstackSlidersModel {
    catstacksData: Record<CatstackPlaneId, { catstack: { count: number } }> | null;
}

/** No per-plane `max`: the slider derives it from `catstacksData`, so the two cannot disagree. */
interface CatstackSlidersScene {
    catstacks: { rowOpen: boolean } & Record<
        CatstackPlaneId,
        { sliceIndex: number; visible: boolean }
    >;
}

interface OutlineToggleScene {
    visibility: VisibilityToggles;
    toggleVisibility(id: VisibilityToggleId, on: boolean): void;
}

/** The store spreads this, so each ref stays top-level and Pinia unwraps it for templates. */
function createCatstackSliders(model: CatstackSlidersModel, scene: CatstackSlidersScene) {
    const rowOpen = computed({
        get: () => scene.catstacks.rowOpen,
        set: (v: boolean) => {
            scene.catstacks.rowOpen = v;
        },
    });

    // Flips rowOpen only: per-plane visibility survives close/reopen, and the render-gate is
    // `rowOpen && visible`, so closing the row still hides the planes.
    function toggleRowOpen() {
        rowOpen.value = !rowOpen.value;
    }

    function maxSliceFor(planeId: CatstackPlaneId): number {
        return model.catstacksData ? model.catstacksData[planeId].catstack.count - 1 : 0;
    }

    // `sliderValue` is inverted (top of slider = first slice); `displayIndex` is 1-based.
    const planeStates = computed<Record<CatstackPlaneId, PlaneState>>(() => {
        const states = {} as Record<CatstackPlaneId, PlaneState>;
        for (const planeId of catstackPlaneIds) {
            const max = maxSliceFor(planeId);
            const sliceIndex = scene.catstacks[planeId].sliceIndex;
            states[planeId] = {
                label: PLANE_LABELS[planeId],
                sliderValue: max - sliceIndex,
                displayIndex: sliceIndex + 1,
                max,
                visible: scene.catstacks[planeId].visible,
            };
        }
        return states;
    });

    function setSliderValue(planeId: CatstackPlaneId, value: number) {
        scene.catstacks[planeId].sliceIndex = maxSliceFor(planeId) - value;
    }

    function toggleVisible(planeId: CatstackPlaneId) {
        scene.catstacks[planeId].visible = !scene.catstacks[planeId].visible;
    }

    function advanceSlice(planeId: CatstackPlaneId, deltaY: number) {
        if (!scene.catstacks[planeId].visible) return;
        scene.catstacks[planeId].sliceIndex = nextSliceIndex(
            deltaY,
            scene.catstacks[planeId].sliceIndex,
            maxSliceFor(planeId),
        );
    }

    return { rowOpen, planeStates, toggleRowOpen, setSliderValue, toggleVisible, advanceSlice };
}

function createOutlineToggle(scene: OutlineToggleScene) {
    const showImplantOutline = computed({
        get: () => scene.visibility['implant-outlines'] === 'on',
        set: (v: boolean) => {
            scene.toggleVisibility('implant-outlines', v);
        },
    });

    // 'implant-outlines' is 'hidden' outside stem/cup mode, so this reduces to "not hidden".
    const showOutlinesVisible = computed(() => scene.visibility['implant-outlines'] !== 'hidden');

    return { showImplantOutline, showOutlinesVisible };
}

export const useViewControlsClusterViewModel = defineStore('view-controls-cluster', () => {
    const planner = usePlannerStore();
    const scene = usePlannerSceneViewModel();

    // stop() is not exposed: the watchers live for the Pinia instance, and tests reset it.
    const presetTracker = useActivePresetTracker(planner, scene);

    return {
        ...createCatstackSliders(planner, scene),
        ...createOutlineToggle(scene),
        activePreset: presetTracker.activePreset,
        setPreset: presetTracker.setPreset,
    };
});
